import { useMutation, useQuery } from "@tanstack/react-query";

import {
	CustomFactorCreateInput,
	CustomFactorUpdateInput,
	createEngagementCustomFactor,
	deleteEngagementCustomFactor,
	getEngagementCustomFactor,
	getEngagementCustomFactors,
	updateEngagementCustomFactor,
} from "../../services/defs/engagement-custom-factors";
import type { GetCustomFactorResponse, GetCustomFactorsResponse } from "../../services/defs/engagement-custom-factors";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

// Query keys
export const customFactorsKeys = {
	all: ["EngagementCustomFactors"] as const,
	list: () => [...customFactorsKeys.all, "list"] as const,
	detail: (id: string) => [...customFactorsKeys.all, "detail", id] as const,
};

// List all custom factors
export const useEngagementCustomFactors = (options?: PYUseQueryOptions<GetCustomFactorsResponse>) => {
	return useQuery({
		queryKey: customFactorsKeys.list(),
		queryFn: () => getEngagementCustomFactors(),
		meta: {
			errorMessage: "There was an error getting the engagement custom factors.",
		},
		...options,
	});
};

// Get single custom factor
export const useEngagementCustomFactor = (id: string, options?: PYUseQueryOptions<GetCustomFactorResponse>) => {
	return useQuery({
		queryKey: customFactorsKeys.detail(id),
		queryFn: () => getEngagementCustomFactor(id),
		enabled: !!id,
		meta: {
			errorMessage: "There was an error getting the custom factor.",
		},
		...options,
	});
};

// Create custom factor
export const useCreateEngagementCustomFactor = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: (factor: CustomFactorCreateInput) => createEngagementCustomFactor(factor),
		...options,
	});
};

// Update custom factor
export const useUpdateEngagementCustomFactor = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: ({ id, factor }: { id: string; factor: CustomFactorUpdateInput }) =>
			updateEngagementCustomFactor(id, factor),
		...options,
	});
};

// Delete custom factor
export const useDeleteEngagementCustomFactor = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: (id: string) => deleteEngagementCustomFactor(id),
		...options,
	});
};
